import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import MasonryGrid from "@/components/MasonryGrid";
import usePageMeta from "@/lib/seo";

export default function CategoryPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/products?category=${slug}`),
      api.get("/categories"),
    ]).then(([p, c]) => {
      setProducts(p.data);
      setCategories(c.data);
    }).finally(() => setLoading(false));
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);
  usePageMeta(category && { title: category.name, description: category.tagline, image: category.image });

  return (
    <div data-testid="category-page">
      {category && (
        <section className="relative h-[40vh] min-h-[300px] overflow-hidden" data-testid="category-hero">
          <img src={category.image} alt={category.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="absolute inset-0 flex flex-col justify-end px-6 md:px-12 pb-12 max-w-7xl mx-auto left-0 right-0">
            <p className="text-[#EBAA86] text-xs uppercase tracking-[0.3em] mb-2">{category.tagline}</p>
            <h1 className="font-heading text-4xl md:text-5xl text-white font-light tracking-tight">{category.name}</h1>
            {category.pinterest_board && (
              <a
                href={category.pinterest_board}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="category-view-board-btn"
                className="mt-5 inline-flex items-center gap-2 self-start bg-[#E60023] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-[#AD081B] transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/></svg>
                View board on Pinterest
              </a>
            )}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`pill-${c.slug}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                c.slug === slug
                  ? "bg-[#2D3032] text-white border-[#2D3032]"
                  : "border-[#E6E1D6] text-[#636769] hover:border-[#2D3032] hover:text-[#2D3032]"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>
        {loading ? (
          <p className="text-[#636769]">Loading…</p>
        ) : (
          <MasonryGrid products={products} />
        )}
      </section>

      {/* Browse other collections */}
      <section className="bg-white border-t border-[#E6E1D6]" data-testid="category-related-collections">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="text-xs uppercase tracking-[0.24em] text-[#EBAA86] mb-2">Keep exploring</p>
          <h2 className="font-heading text-2xl md:text-3xl text-[#2D3032] font-normal mb-10">Browse other collections.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.filter((c) => c.slug !== slug).slice(0, 4).map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                data-testid={`related-cat-${c.slug}`}
                className="group relative overflow-hidden bg-[#2D3032] aspect-[2/3] gc-card gc-fade"
              >
                <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.06] transition-all duration-700 gc-sunwash" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#EBAA86] mb-1">{c.tagline}</p>
                  <h3 className="font-heading text-base md:text-lg font-medium leading-tight">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
